/**
 * Carte de la page de partage — F-CNF-07, F-CNF-08.
 *
 * Un seul marqueur : la position du véhicule. Ni épingle de destination ni
 * itinéraire tracé — la page ne reçoit plus la destination (décision du porteur
 * du 22 octobre 2026, SA-16 : position et estimation d'arrivée, rien d'autre),
 * et un tracé donnerait à un tiers plus que ce que cette page a le droit de dire.
 *
 * Sans position, il n'y a rien à centrer : la carte n'est ni créée ni même
 * demandée à Google tant que le véhicule n'a pas de position connue — un
 * chargement de carte est facturé à l'appel, il ne se gaspille pas sur un fond
 * vide. Une fois créée, elle reste montée : une coupure passagère du signal GPS
 * n'en recrée pas une seconde à chaque retour.
 *
 * Sans clé Google Maps, la carte s'efface : l'heure d'arrivée reste utile sans
 * elle (arbitrage encore ouvert, CLAUDE.md §4).
 */
import { useEffect, useRef, useState } from 'react';

import type { Traducteur } from '@qanbio/i18n/partage';

import type { PositionGeo } from './api';

declare global {
  interface Window {
    google?: typeof google;
  }
}

let chargement: Promise<void> | null = null;

function chargerGoogleMaps(cle: string): Promise<void> {
  if (window.google?.maps !== undefined) return Promise.resolve();
  chargement ??= new Promise((resoudre, rejeter) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(cle)}&loading=async`;
    script.async = true;
    script.onload = () => resoudre();
    script.onerror = () => {
      chargement = null;
      rejeter(new Error('Le chargement de Google Maps a échoué'));
    };
    document.head.append(script);
  });
  return chargement;
}

export interface CarteProps {
  readonly cleApi: string | undefined;
  readonly position: PositionGeo | null;
  readonly couleurAccent: string;
  /** Contour du marqueur : le texte sur accent du jeu de couleurs, jamais un blanc littéral. */
  readonly couleurContour: string;
  readonly traducteur: Traducteur;
}

/** Zoom d'une rue et de ses abords : on suit un véhicule, pas une ville. */
const ZOOM_SUIVI = 16;

export function Carte({ cleApi, position, couleurAccent, couleurContour, traducteur }: CarteProps) {
  const conteneurRef = useRef<HTMLDivElement>(null);
  const carteRef = useRef<google.maps.Map | null>(null);
  const marqueurRef = useRef<google.maps.Marker | null>(null);
  const [pret, setPret] = useState(false);
  const [enEchec, setEnEchec] = useState(false);
  const aUnePosition = position !== null;

  useEffect(() => {
    // Rien à demander à Google tant qu'aucune position n'est connue.
    if (!aUnePosition) return;
    if (cleApi === undefined || cleApi === '') {
      setEnEchec(true);
      return;
    }
    let annule = false;
    chargerGoogleMaps(cleApi)
      .then(() => {
        if (!annule) setPret(true);
      })
      .catch(() => {
        if (!annule) setEnEchec(true);
      });
    return () => {
      annule = true;
    };
  }, [cleApi, aUnePosition]);

  useEffect(() => {
    const maps = window.google?.maps;
    if (!pret || maps === undefined || conteneurRef.current === null) return;

    if (position === null) {
      // Position perdue : un point périmé induirait en erreur, on le retire. La
      // carte, elle, reste là où elle était.
      marqueurRef.current?.setMap(null);
      marqueurRef.current = null;
      return;
    }

    const point = { lat: position.latitude, lng: position.longitude };
    carteRef.current ??= new maps.Map(conteneurRef.current, {
      center: point,
      zoom: ZOOM_SUIVI,
      disableDefaultUI: true,
      zoomControl: true,
      clickableIcons: false,
    });
    const carte = carteRef.current;

    if (marqueurRef.current === null) {
      marqueurRef.current = new maps.Marker({
        map: carte,
        position: point,
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: couleurAccent,
          fillOpacity: 1,
          strokeColor: couleurContour,
          strokeWeight: 2,
        },
        zIndex: 10,
      });
    } else {
      marqueurRef.current.setPosition(point);
    }

    // On ne recentre que si le véhicule sort du cadre : un visiteur qui a
    // déplacé ou zoomé la carte ne se la fait pas ramener à chaque relevé.
    if (carte.getBounds()?.contains(point) !== true) carte.panTo(point);
  }, [pret, position, couleurAccent, couleurContour]);

  const etiquette = traducteur.traduire('partage.carte.etiquette');

  if (enEchec) {
    return (
      <div className="carte carte-indisponible" role="img" aria-label={etiquette}>
        <span>{traducteur.traduire('partage.carte_indisponible')}</span>
      </div>
    );
  }

  return <div className="carte" ref={conteneurRef} aria-label={etiquette} />;
}
